import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';

export type ProfilePasswordUpdate = {
  newPassword: string;
  newPasswordCompare: string;
  oldPassword: string;
};

export type ProfileUpdate = {
  address: string;
  city: string;
  company_name: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  postal_code: string;
  tax_code: string;
};

type Profile = {
  address: string;
  birth_date: string;
  city: string;
  company_name: string;
  country_code: string;
  email: string;
  first_name: string;
  gender: string;
  last_name: string;
  license_expiry_date: string;
  phone_number: string;
  postal_code: string;
  qr: string;
  role: string;
  tax_code: string;
};

type ProfileState = {
  apiErrors: ApiErrors;
  current: Profile | null;
  loaders: Loaders;
  passwordSaved: boolean;
};

const profileApiPrefix = '/users/me';

// TODO: use object instead of  array
const initialState: ProfileState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  current: null,
  loaders: {
    isLoading: false,
    isSaving: false
  },
  passwordSaved: false
};

export const fetchProfile = createAsyncThunk<Profile, null, { rejectValue: string }>(
  'profiles/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${profileApiPrefix}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const updatePassword = createAsyncThunk<boolean, ProfilePasswordUpdate, { rejectValue: string }>(
  'profiles/updatePassword',
  async (pwd, { rejectWithValue }) => {
    try {
      const { oldPassword, newPassword, newPasswordCompare } = pwd;
      await axiosInstance.put(`${profileApiPrefix}/password`, {
        new_password: newPassword,
        new_password_compare: newPasswordCompare,
        old_password: oldPassword
      });
      return true;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('account.old password wrong'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const updateProfile = createAsyncThunk<ProfileUpdate, ProfileUpdate, { rejectValue: string }>(
  'profiles/updateProfile',
  async (profile, { rejectWithValue }) => {
    try {
      await axiosInstance.put(`${profileApiPrefix}`, profile);
      return profile;
    } catch (err) {
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const profilesSlice = createSlice({
  initialState,
  name: 'profile',
  reducers: {
    resetProfile: state => {
      state.current = null;
    },
    closeError: state => {
      state.apiErrors = {};
      state.passwordSaved = false;
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchProfile.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.current = payload;
      })
      .addCase(fetchProfile.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchProfile.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(updatePassword.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.passwordSaved = true;
      })
      .addCase(updatePassword.pending, state => {
        state.passwordSaved = false;
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(updatePassword.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(updateProfile.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        if (state.current) {
          state.current = { ...state.current, ...payload };
        }
      })
      .addCase(updateProfile.pending, state => {
        state.passwordSaved = false;
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(updateProfile.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = profilesSlice;
export { reducer as profile };
export const { closeError, resetProfile } = actions;

export const getProfile = (state: RootState) => state.profile.current;
export const getPasswordSaved = (state: RootState) => state.profile.passwordSaved;
export const getProfileLoaders = (state: RootState) => state.profile.loaders;
export const getProfileErrors = (state: RootState) => state.profile.apiErrors;
